/**
 * SPIELHÖLLE palette — the single source of colour truth.
 * ART-BIBLE.md §1. No hex literal may appear anywhere else in the kit.
 *
 * "1987 Autobahn-Raststätte arcade after midnight, drawn as anime."
 */

import { Color } from 'three';

export type Triple = { lit: number; base: number; shade: number };

/** the room's darkness — shadows are cool-violet, never neutral, never black */
export const NACHT = {
  void: 0x08070e,
  deep: 0x12101c,
  base: 0x1c1930,
  lift: 0x2b2745,
} as const;

/** kitsch chassis colours — the real identity of the game */
export const CREME: Triple = { lit: 0xf6e9ce, base: 0xe2cda4, shade: 0xa8906a };
export const BAKELIT: Triple = { lit: 0xa0673c, base: 0x6b4526, shade: 0x3a2418 };
export const GUMMI: Triple = { lit: 0xff9a4d, base: 0xf2762e, shade: 0xa8451a };
export const SENF: Triple = { lit: 0xffd873, base: 0xe8b33c, shade: 0x9a6f1e };
export const SEEGRUEN: Triple = { lit: 0x63c9ae, base: 0x2e9c86, shade: 0x17564c };
export const BLUTROT: Triple = { lit: 0xff6b5e, base: 0xc93a2e, shade: 0x7a1d16 };
export const FILZ: Triple = { lit: 0x3e7f5e, base: 0x276046, shade: 0x143528 };

/** neon — light sources only. never a chassis colour. */
export const NEON = {
  magenta: { core: 0xff2e88, halo: 0xff8fc0 },
  cyan: { core: 0x2ee8ff, halo: 0xb6fbff },
  amber: { core: 0xffb23d, halo: 0xffdca0 },
  violet: { core: 0x9b4dff, halo: 0xd2aeff },
  gruen: { core: 0x6fe87a, halo: 0xc2ffc6 },
} as const;

export type NeonName = keyof typeof NEON;

/** the contract's four token materials */
export const MATERIALS = {
  kupfer: { lit: 0xe89a5e, base: 0xc4703a, shade: 0x6e3a1c },
  silber: { lit: 0xeff4f8, base: 0xc9d2dc, shade: 0x6e7a88 },
  gold: { lit: 0xffe99a, base: 0xf5c542, shade: 0x9a6b12 },
  obsidian: { lit: 0x3b3550, base: 0x221e2e, shade: 0x0c0a14 },
} as const satisfies Record<string, Triple>;

/** ink & accents */
export const INK = 0x150f1c;
export const INK_WARM = 0x2a1a16;
export const HALFTONE = 0x0d0a16;
export const RIM = 0xa8c8ff;
export const STAUB = 0xd9cdba;
export const PAPIER = 0xf4eedc;

/** per-tag signature colours (ART-BIBLE §4.1) */
export const TAG_COLOR = {
  Magnet: NEON.cyan.core,
  Geist: 0xd8f6ff,
  Gummi: GUMMI.base,
  Kleber: SENF.base,
  Spalt: NEON.cyan.core,
  Gold: MATERIALS.gold.base,
  Fluch: NEON.violet.core,
  Feuer: 0xff7a2e,
  Gluecks: NEON.gruen.core,
  Schwer: BAKELIT.lit,
  Zins: NEON.amber.core,
  Echo: NEON.magenta.core,
} as const;

/** cabinet identity accent — every machine owns exactly one kitsch hue + one neon */
export const MACHINE_LOOK = {
  pachinko: { chassis: GUMMI, accent: 'cyan' as NeonName, name: 'KUGELREGEN' },
  muenzschieber: { chassis: SEEGRUEN, accent: 'amber' as NeonName, name: 'MÜNZSCHIEBER' },
  bandit: { chassis: BLUTROT, accent: 'magenta' as NeonName, name: 'EINARMIGER BANDIT' },
  roulette: { chassis: BAKELIT, accent: 'gruen' as NeonName, name: 'ROULETTE' },
  greifer: { chassis: SENF, accent: 'violet' as NeonName, name: 'PREISTHEKE' },
  /**
   * The Saalchef's table. Cream chassis with violet neon, because violet is this game's
   * House colour (§1.3: "Fluch smolder, VERDACHT, Der Croupier") — the one cabinet on the
   * floor that is not yours. It reads as the same arcade and as the wrong end of it.
   */
  flipper: { chassis: CREME, accent: 'violet' as NeonName, name: 'DER SAALCHEF' },
} as const;

// ---------------------------------------------------------------- helpers

const _c = new Color();

/** hex → THREE.Color (new instance) */
export function col(hex: number): Color {
  return new Color(hex);
}

/** hex → [r,g,b] in linear-ish 0..1 (sRGB decoded, matching three's colour management) */
export function rgb(hex: number): [number, number, number] {
  _c.setHex(hex, 3001 as never); // SRGBColorSpace handled by setHex default
  return [_c.r, _c.g, _c.b];
}

/** mix two hex colours in sRGB space, t=0 → a */
export function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/** css string for canvas work */
export function css(hex: number, alpha = 1): string {
  const r = (hex >> 16) & 255, g = (hex >> 8) & 255, b = hex & 255;
  return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Outline colour for a body colour (ART-BIBLE §2.4):
 * outlines carry a memory of the thing they surround.
 */
export function inkFor(bodyHex: number, warm = false): number {
  const base = warm ? INK_WARM : INK;
  const dim = mixHex(0x000000, bodyHex, 0.35);
  return mixHex(base, dim, 0.25);
}
